/**
 * 🎯 SHARED: Checkout with Delivery Zones Integration
 *
 * Dynamischer Wrapper für useCheckoutWithDeliveryZones mit automatischer Projekt-Erkennung
 * Integriert Cart Store mit Delivery Zone Validation für alle Projekte
 */
import { computed, watch } from 'vue';
import { useCartStore } from '@shared';
import { useCheckoutWithDeliveryZones } from './useCheckoutWithDeliveryZones';
import { useProjectConfig } from './useProjectConfig';
/**
 * Enhanced Checkout mit Delivery Zones - Projekt-agnostisch
 * Verwendet useProjectConfig() für automatische Projekt-Erkennung
 */
export function useCheckoutWithZones() {
    const cartStore = useCartStore();
    const { config } = useProjectConfig();
    // ✅ Transformiere Cart Store Data für shared composable
    const cartStoreData = computed(() => ({
        items: cartStore.items,
        deliveryType: cartStore.deliveryType,
        deliveryAddress: {
            street: cartStore.deliveryAddress.street || '',
            city: cartStore.deliveryAddress.city || '',
            postalCode: cartStore.deliveryAddress.postalCode || ''
        }
    }));
    // ✅ Verwende shared composable mit dynamischer project ID
    const checkoutWithZones = useCheckoutWithDeliveryZones(cartStoreData.value, config.project.id);
    // ✅ Automatische Preisberechnung bei Adressänderung
    watch(() => cartStoreData.value.deliveryAddress, async (newAddress, oldAddress) => {
        // Nur wenn sich die vollständige Adresse geändert hat
        const newFormatted = `${newAddress.street}, ${newAddress.postalCode} ${newAddress.city}`.trim();
        const oldFormatted = `${oldAddress?.street || ''}, ${oldAddress?.postalCode || ''} ${oldAddress?.city || ''}`.trim();
        if (newFormatted !== oldFormatted &&
            newAddress.street && newAddress.city && newAddress.postalCode &&
            cartStore.deliveryType === 'delivery') {
            console.log(`🔄 [${config.project.id}] Delivery address changed, recalculating pricing...`);
            await checkoutWithZones.calculatePricingWithDeliveryAddress();
        }
    }, { deep: true });
    // ✅ Automatische Preisberechnung bei Delivery Type Änderung
    watch(() => cartStore.deliveryType, async (newType, oldType) => {
        if (newType !== oldType) {
            console.log(`🔄 [${config.project.id}] Delivery type changed, recalculating pricing...`);
            await checkoutWithZones.calculatePricingWithDeliveryAddress();
        }
    });
    // ✅ Helper: Vollständige Adresse formatieren
    const getFullDeliveryAddress = () => {
        const addr = cartStore.deliveryAddress;
        if (!addr.street || !addr.city || !addr.postalCode) {
            return null;
        }
        return `${addr.street.trim()}, ${addr.postalCode.trim()} ${addr.city.trim()}`;
    };
    // ✅ Helper: Schnelle Lieferzonenvalidierung (ohne Cart Update)
    // ✅ FIX: Removed unused parameters - validation uses cart store data
    const quickValidateAddress = async () => {
        return await checkoutWithZones.validateCurrentDeliveryAddress();
    };
    // ✅ Helper: Aktualisiere Cart Store mit neuer Adresse
    const updateDeliveryAddress = (addressData) => {
        cartStore.deliveryAddress.street = addressData.street;
        cartStore.deliveryAddress.city = addressData.city;
        cartStore.deliveryAddress.postalCode = addressData.postalCode;
        // Trigger reactivity by updating a reactive property
        cartStore.deliveryAddress = { ...cartStore.deliveryAddress };
    };
    return {
        // Original Cart Store für direkte Nutzung
        cartStore,
        // Enhanced Checkout Funktionalität
        ...checkoutWithZones,
        // Shared Helpers
        getFullDeliveryAddress,
        quickValidateAddress,
        updateDeliveryAddress,
        // Convenient computed values
        hasValidDeliveryAddress: computed(() => {
            const addr = cartStore.deliveryAddress;
            return !!(addr.street?.trim() && addr.city?.trim() && addr.postalCode?.trim());
        }),
        canShowDeliveryFee: computed(() => {
            return cartStore.deliveryType === 'delivery' &&
                checkoutWithZones.deliveryFeeInfo.value?.feeCent !== undefined;
        }),
        deliveryFeeDisplay: computed(() => {
            const feeInfo = checkoutWithZones.deliveryFeeInfo.value;
            if (!feeInfo)
                return null;
            if (feeInfo.isFree) {
                return {
                    text: 'Kostenlose Lieferung',
                    amount: '0,00 €',
                    class: 'text-green-600'
                };
            }
            else {
                const amount = feeInfo.feeEuro.toFixed(2).replace('.', ',') + ' €';
                const reason = feeInfo.isZoneBased ?
                    `Liefergebühr${feeInfo.zoneName ? ` (${feeInfo.zoneName})` : ''}` :
                    'Liefergebühr';
                return {
                    text: reason,
                    amount,
                    class: 'text-gray-900'
                };
            }
        })
    };
}
